import SrDocumentDelete from './SrDocumentDelete'
import SrDocumentTable, {
  srDocumentsColumnsAtom,
  useResetSrDocuments,
} from './SrDocumentTable'
import SrDocumentView from './SrDocumentView'
import { fetchSrDocumentColumns } from 'actions/srDocuments/srDocumentColumnActions'
import { fetchSrDocumentPropertyOptions } from 'actions/srDocuments/srDocumentPropertyActions'
import { fetchSrDocumentViews } from 'actions/srDocuments/srDocumentViewActions'
import Modal from 'components/common/Modal'
import Section from 'components/common/Section'
import { useAuth } from 'hooks/useAuth.js'
import useModal from 'hooks/useModal'
import { useSetAtom } from 'jotai'
import { MDBIcon } from 'mdbreact'
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  isUserGuest,
  isUserNotReadOnly,
  isUserNotReadOnlyForSiteReadiness,
} from 'utils/authUtils'
import { defaultSrDocumentColumns } from 'utils/tableUtils'

const fetchTableLink = (type, item) => {
  switch (type) {
    case 'show':
      return `/sr-documents/${item.id}`
    case 'edit':
      return `/sr-documents/${item.id}/edit`
    default:
      return null
  }
}

const SrDocumentList = () => {
  //// HOOKS.
  const { user } = useAuth() ?? {}
  const { user_level, is_admin } = user ?? {}
  const dispatch = useDispatch()
  const resetSrDocuments = useResetSrDocuments(dispatch)
  const {
    modalOpen,
    headerText,
    setHeaderText,
    formId,
    setFooter,
    setActions,
    selected,
    setSelected,
    setModalState,
  } = useModal()

  //// GLOBAL STATE.
  const [setSrDocumentsColumns] = [useSetAtom(srDocumentsColumnsAtom)]

  const {
    srDocumentProperties: { srDocumentPropertyOptions },
    srDocumentColumns: { srDocumentColumns: srDocumentColumnsFromRedux },
    srDocuments: {
      srDocumentsAvailable,
      lastAction,
      isLoadingMore: isLoadingMoreSrDocuments,
    },
  } = useSelector(
    ({ srDocumentProperties, srDocumentColumns, srDocuments }) => ({
      srDocumentProperties,
      srDocumentColumns,
      srDocuments,
    })
  )

  //// LOCAL STATE.
  const [isInit, setIsInit] = useState(true)

  //// EFFECTS.
  useEffect(
    () => {
      setIsInit(true)
      resetSrDocuments({
        srDocumentsTableData: {
          columns: [
            ...defaultSrDocumentColumns,
            {
              label: '',
              field: 'buttons',
              sort: 'disabled',
              minimal: 'lg',
            },
          ],
          rows: [],
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    setSrDocumentsColumns(srDocumentColumnsFromRedux)
  }, [srDocumentColumnsFromRedux, setSrDocumentsColumns])

  useEffect(
    () => {
      // load init data
      if (user) {
        Promise.all(
          [
            fetchSrDocumentPropertyOptions(),
            fetchSrDocumentViews(),
            fetchSrDocumentColumns(user),
          ].map((action) => dispatch(action))
        ).finally(() => {
          setIsInit(false)
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  )

  //// FUNCTIONS.
  const toggleModal = (e, type, item) => {
    if (e) e.preventDefault()

    if (type === 'delete') {
      setHeaderText('Delete Site Readiness Document')
      setFooter(true)
      setActions([
        {
          text: 'Delete',
          color: 'danger',
          additionalProps: {
            form: formId,
            type: 'submit',
          },
        },
      ])
      setSelected(item)
    }

    if (type) {
      setModalState(true)
      return true
    } else {
      setModalState(false)
      return false
    }
  }

  //// RENDER.
  return (
    <Section
      title={'Site Readiness Document List'}
      menu={
        isUserNotReadOnly(user_level) || isUserGuest(user_level)
          ? {
              order: 2,
              show: true,
              linkItems: !isUserNotReadOnlyForSiteReadiness(user_level)
                ? []
                : [
                    {
                      name: (
                        <>
                          <MDBIcon icon="plus" /> Add Site Readiness Document
                        </>
                      ),
                      url: '/sr-documents/new',
                      additionalProps: {
                        active: true,
                      },
                    },
                  ],
              leftSide: <SrDocumentView editable />,
            }
          : void 0
      }
      loading={isInit}
    >
      {!isInit && (
        <SrDocumentTable
          srDocumentsAvailable={srDocumentsAvailable}
          isLoadingMore={isLoadingMoreSrDocuments}
          lastAction={lastAction}
          srDocumentPropertyOptions={srDocumentPropertyOptions}
          fetchTableLink={fetchTableLink}
          toggleModal={toggleModal}
          willSaveColumns
        />
      )}
      {is_admin ? (
        <Modal
          isOpen={modalOpen}
          toggle={toggleModal}
          headerText={headerText}
          includeFooter={true}
          actionButtons={[
            {
              text: 'Delete',
              color: 'danger',
              additionalProps: {
                form: formId,
                type: 'submit',
              },
            },
          ]}
        >
          <SrDocumentDelete
            formId={formId}
            selected={selected}
            closeModal={() => setModalState(false)}
          />
        </Modal>
      ) : (
        void 0
      )}
    </Section>
  )
}

export default SrDocumentList
